import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { addToast, Button, Card, CardBody, CardHeader, Chip } from '@structural-climbing/ui'
import { CategoryFormModal } from './CategoryFormModal'
import { ProductFormModal } from './ProductFormModal'
import { ProductImage } from './ProductImage'
import { formatMoney } from './money'
import type { Product, ProductCategory } from './types'

interface Props {
  categories: ProductCategory[]
  products: Product[]
  currency: string
  onRefresh: () => void
}

/** The owner's side: what the gym sells, what it costs and how much is left. */
export function CatalogueTab({ categories, products, currency, onRefresh }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [categoryModal, setCategoryModal] = useState<{ target: ProductCategory | null } | null>(
    null,
  )
  const [productModal, setProductModal] = useState<{
    target: Product | null
    categoryId: string | null
  } | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)

  async function remove(path: string): Promise<Response> {
    const token = await getAccessTokenSilently()
    return fetch(`${apiUrl}${path}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
  }

  async function deleteCategory(category: ProductCategory) {
    if (!window.confirm(t('products.confirmDeleteCategory', { name: category.name }))) return
    setPendingId(category.id)
    try {
      const res = await remove(`/product-categories/${category.id}`)
      if (res.ok) {
        onRefresh()
        return
      }
      const body = (await res.json().catch(() => ({}))) as { code?: string }
      addToast({
        title:
          body.code === 'CATEGORY_NOT_EMPTY'
            ? t('products.categoryNotEmpty')
            : t('products.errorSaveCategory'),
        color: 'danger',
      })
    } catch {
      addToast({ title: t('products.errorSaveCategory'), color: 'danger' })
    } finally {
      setPendingId(null)
    }
  }

  async function deleteProduct(product: Product) {
    if (!window.confirm(t('products.confirmDeleteProduct', { name: product.name }))) return
    setPendingId(product.id)
    try {
      const res = await remove(`/products/${product.id}`)
      if (!res.ok) {
        addToast({ title: t('products.errorDeleteProduct'), color: 'danger' })
        return
      }
      // A product with sales is taken off sale and kept, and the owner who
      // pressed Delete should know why it is still on the list.
      const { archived } = (await res.json()) as { archived?: boolean }
      if (archived) {
        addToast({ title: t('products.archivedNote', { name: product.name }), color: 'warning' })
      }
      onRefresh()
    } catch {
      addToast({ title: t('products.errorDeleteProduct'), color: 'danger' })
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button color="primary" size="sm" onPress={() => setCategoryModal({ target: null })}>
          {t('products.newCategory')}
        </Button>
      </div>

      {categories.length === 0 && (
        <p className="py-10 text-center text-sm text-foreground/50">{t('products.noCategories')}</p>
      )}

      {categories.map((category) => {
        const rows = products.filter((product) => product.category_id === category.id)
        return (
          <Card key={category.id}>
            <CardHeader className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="min-w-0 truncate text-base font-semibold">{category.name}</h2>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  size="sm"
                  color="primary"
                  variant="flat"
                  onPress={() => setProductModal({ target: null, categoryId: category.id })}
                >
                  {t('products.newProduct')}
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  aria-label={`${t('common.edit')} ${category.name}`}
                  onPress={() => setCategoryModal({ target: category })}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label={`${t('common.delete')} ${category.name}`}
                  isLoading={pendingId === category.id}
                  isDisabled={pendingId !== null}
                  onPress={() => void deleteCategory(category)}
                >
                  {t('common.delete')}
                </Button>
              </div>
            </CardHeader>
            <CardBody className="pt-0">
              {rows.length === 0 ? (
                <p className="py-4 text-center text-sm text-foreground/40">
                  {t('products.emptyCategory')}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {rows.map((product) => (
                    <div
                      key={product.id}
                      className="flex flex-col gap-3 rounded-lg border border-divider p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <ProductImage
                          name={product.name}
                          imageUrl={product.image_url}
                          className="h-12 w-12 shrink-0 rounded-md"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium">{product.name}</p>
                            {!product.is_active && (
                              <Chip size="sm" variant="flat">
                                {t('products.notOnSale')}
                              </Chip>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-foreground/50">
                            {formatMoney(product.sell_price, currency)}
                            {product.buy_price != null &&
                              ` · ${t('products.buyPriceRow', { price: formatMoney(product.buy_price, currency) })}`}
                            {' · '}
                            <span className={product.stock < 0 ? 'font-medium text-danger' : ''}>
                              {t('products.stockRow', { n: product.stock })}
                            </span>
                          </p>
                          {product.stock < 0 && (
                            <p className="mt-0.5 text-xs text-danger">{t('products.recount')}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          size="sm"
                          variant="flat"
                          aria-label={`${t('common.edit')} ${product.name}`}
                          isDisabled={pendingId !== null}
                          onPress={() =>
                            setProductModal({ target: product, categoryId: product.category_id })
                          }
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          color="danger"
                          aria-label={`${t('common.delete')} ${product.name}`}
                          isLoading={pendingId === product.id}
                          isDisabled={pendingId !== null}
                          onPress={() => void deleteProduct(product)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )
      })}

      <CategoryFormModal
        isOpen={categoryModal !== null}
        editTarget={categoryModal?.target ?? null}
        onClose={() => setCategoryModal(null)}
        onSaved={onRefresh}
      />
      <ProductFormModal
        isOpen={productModal !== null}
        editTarget={productModal?.target ?? null}
        categories={categories}
        defaultCategoryId={productModal?.categoryId ?? null}
        currency={currency}
        onClose={() => setProductModal(null)}
        onSaved={onRefresh}
      />
    </div>
  )
}
